import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useStore } from "@nanostores/react";
import { type ErrorType } from "@vega-api/configs/customClientQuery";
import { $organization } from "@vega-api/custom/stores/organisations.store";
import { useSearchMessagingParticipants } from "@vega-api/generated/coinvest-deal-messaging-participants/coinvest-deal-messaging-participants";
import {
  createConversation,
  listConversations,
} from "@vega-api/generated/messaging-conversations-v1/messaging-conversations-v1";
import { sendMessage } from "@vega-api/generated/messaging-messages-v1/messaging-messages-v1";
import { MessageContentType } from "@vega-api/generated/types/messageContentType";
import { TextareaInput } from "@vega-core/components/Form/Controls/Textarea/TextareaInput";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";
import { trackCoInvestQuestionAsked } from "@vega-core/utils/posthog/coInvestAnalytics";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";
import {
  COINVEST_MESSAGING_PRODUCT_ID,
  invalidateConversationQueries,
} from "@vega-modules/scenes/CoInvestments/messaging/conversationsData";

type AskQuestionModalProps = {
  onClose: () => void;
  dealId: string;
  dealOpportunityId: string;
  dealName: string;
};

export const AskQuestionModal = ({
  onClose,
  dealId,
  dealOpportunityId,
  dealName,
}: AskQuestionModalProps) => {
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();
  const queryClient = useQueryClient();
  const organization = useStore($organization);
  const [questionBody, setQuestionBody] = useState("");

  const participantsQuery = useSearchMessagingParticipants(dealOpportunityId);

  const askQuestionMutation = useMutation({
    mutationFn: async () => {
      const participantsData = participantsQuery.data?.data;

      if (!participantsData) throw new Error("Participants not loaded");

      const messageContent = questionBody.trim();

      const existingConversation = await listConversations({
        metadata: JSON.stringify({ dealOpportunityId }),
        pageSize: 1,
      });

      if (existingConversation.data.length > 0) {
        return sendMessage(existingConversation.data[0].id, {
          messageContent,
          contentType: MessageContentType.TEXT,
        });
      }

      const participants = [
        ...participantsData.gpUsers,
        ...participantsData.lpUsers,
      ].map((participant) => participant.userId);

      return createConversation({
        productId: COINVEST_MESSAGING_PRODUCT_ID,
        meta: {
          dealId,
          dealOpportunityId,
          dealName,
          limitedPartnerName: organization?.name ?? "",
        },
        participants,
        initialMessage: {
          messageContent,
          contentType: MessageContentType.TEXT,
        },
      });
    },
    onSuccess: () => {
      trackCoInvestQuestionAsked({ dealOpportunityId });
      invalidateConversationQueries(queryClient);
      showToast({
        title: m.status_success(),
        description: m.coInvestments_messages_askQuestion_successToast(),
        variant: "success",
      });
      onClose();
    },
    onError: (error) => {
      showErrorToast(error as ErrorType<unknown>, {
        defaultMessage: m.coInvestments_messages_askQuestion_errorToast(),
      });
    },
  });

  const canSubmit =
    !!questionBody.trim() &&
    !askQuestionMutation.isPending &&
    participantsQuery.isSuccess;

  const handleSubmit = () => {
    if (!canSubmit) return;
    askQuestionMutation.mutate();
  };

  return (
    <ActionaryModal
      ModalButtonsGroupProps={{
        mainAction: handleSubmit,
        mainActionText: m.btn_submit(),
        disabled: !canSubmit,
        secondaryAction: onClose,
        secondaryActionText: m.btn_cancel(),
      }}
      dataTestId="coinvest-ask-question-modal"
      description={m.coInvestments_messages_askQuestion_description()}
      fluidHeight
      open
      setOpen={onClose}
      title={m.coInvestments_messages_askQuestion_title()}
    >
      <TextareaInput
        dataTestId="coinvest-ask-question-input"
        fullWidth
        name="questionBody"
        onChange={(event) => setQuestionBody(event.target.value)}
        placeholder={m.coInvestments_messages_askQuestion_placeholder()}
        rows={8}
        value={questionBody}
        withBorder
      />
    </ActionaryModal>
  );
};
